import { ANY, DELIVERY_METHOD_NAME_MAP } from '../constants';
import { getApiConfigs, captureUndefinedPortalError, apiClientWithMiddleware } from './utils';
import { debug } from '../utils/DebugUtil';
import { isTrashTab, isUnreadTab } from '../channels/sidebar/utils/TabUtils';
const NOTIFICATION_STATION_API_V1_URL = 'notification-station/general/v1/notifications';
export const NOTIFICATION_STATION_API_V3_URL = 'notification-station/general/v3/notifications';
const TURN_OFF_API_PREFERENCE = `notification/preferences/v2/preferences/turn-off/preference`;
const TURN_ON_API_PREFERENCE = `notification/preferences/v2/preferences/turn-on/preference`;
const TURN_OFF_SOUND_URL = 'notification/preferences/v2/preferences/turn-off/sound';
export function markAsDelivered({
  notificationId,
  deliveryMethod
}) {
  const url = `${NOTIFICATION_STATION_API_V1_URL}/delivered/${notificationId}/${deliveryMethod}`;
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve(null);
  }
  return apiClientWithMiddleware(clientConfig).put(url, queryConfig).catch(error => {
    if (error.status !== 0) {
      debug(`Error while marking ${deliveryMethod} notification id:${notificationId} as delivered`, error);
      throw error;
    }
  });
}
export function markNotificationAsReadApi({
  notificationId,
  deliveryMethod
}) {
  const url = `${NOTIFICATION_STATION_API_V1_URL}/mark/${notificationId}`;
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve(null);
  }
  return apiClientWithMiddleware(clientConfig).put(url, {
    query: {
      portalId,
      deliveryMethod
    }
  }).catch(error => {
    if (error.status !== 0) {
      debug(`Error while marking ${deliveryMethod} notification id:${notificationId} as read`, error);
      throw error;
    }
    return Promise.resolve(null);
  });
}
export const turnOffNotification = ({
  id,
  deliveryMethod
}) => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve(null);
  }
  return apiClientWithMiddleware(clientConfig).postWithResponse(`${TURN_OFF_API_PREFERENCE}?portalId=${portalId}`, {
    data: {
      notificationId: id,
      deliveryMethod: DELIVERY_METHOD_NAME_MAP[deliveryMethod]
    }
  }).catch(error => {
    if (error.status !== 0) {
      debug(`Error while turning off ${deliveryMethod} notification (id: ${id})`, error);
      throw error;
    }
  });
};
export const turnOnNotification = ({
  id,
  deliveryMethod
}) => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve(null);
  }
  return apiClientWithMiddleware(clientConfig).postWithResponse(`${TURN_ON_API_PREFERENCE}?portalId=${portalId}`, {
    data: {
      notificationId: id,
      deliveryMethod: DELIVERY_METHOD_NAME_MAP[deliveryMethod]
    }
  }).catch(error => {
    if (error.status !== 0) {
      debug(`Error while turning on ${deliveryMethod} notification (id: ${id})`, error);
      throw error;
    }
  });
};
export const turnOffNotificationSound = ({
  id,
  deliveryMethod
}) => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve(null);
  }
  return apiClientWithMiddleware(clientConfig).postWithResponse(`${TURN_OFF_SOUND_URL}?portalId=${portalId}`, {
    data: {
      notificationId: id,
      deliveryMethod: DELIVERY_METHOD_NAME_MAP[deliveryMethod]
    }
  }).catch(error => {
    if (error.status !== 0) {
      debug(`Error while turning sound off for ${deliveryMethod} notification (id: ${id})`, error);
      throw error;
    }
  });
};
export const getV3NotificationsList = ({
  filterOption,
  beforeId,
  sidebarTabState: tabState
}) => {
  const {
    queryConfig,
    clientConfig,
    locale
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  const showUnreadOnly = isUnreadTab(tabState);
  const showArchivedOnly = isTrashTab(tabState);
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve({
      hasMore: false,
      notifications: []
    });
  }
  const url = isTrashTab(tabState) ? '/list-deleted?' : '/list?';
  const queryParams = Object.assign({
    portalId,
    types: filterOption !== ANY ? [filterOption] : [],
    beforeId,
    locale
  }, showArchivedOnly ? {} : {
    showUnreadOnly
  });
  return apiClientWithMiddleware(clientConfig).get(`${NOTIFICATION_STATION_API_V3_URL}${url}`, {
    query: queryParams
  }).catch(error => {
    if (error.status !== 0) {
      debug(`Error fetching unread notifications list`, error);
    }
    throw error;
  });
};
export const getNotificationTypes = () => {
  const {
    queryConfig,
    clientConfig,
    locale
  } = getApiConfigs();
  const {
    portalId
  } = queryConfig.query;
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.resolve([]);
  }
  return apiClientWithMiddleware(clientConfig).get(`notification-station/general/v2/info/types`, {
    query: {
      filter: 'USER_ONLY',
      locale,
      portalId
    }
  }).catch(error => {
    if (error.status !== 0) {
      debug(`Error fetching notifications types`, error);
    }
    return [];
  });
};
export const batchActionOnSomeSidebarNotifications = ({
  action,
  notificationIds
}) => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.reject(new Error('Undefined portal'));
  }
  const {
    query
  } = queryConfig;
  return apiClientWithMiddleware(clientConfig).post(`${NOTIFICATION_STATION_API_V3_URL}/batch/action`, {
    data: {
      action,
      includedIds: notificationIds
    },
    query
  });
};
export const batchActionOnAllSidebarNotifications = ({
  action,
  notificationIds,
  sidebarState,
  filterType
}) => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.reject(new Error('Undefined portal'));
  }
  const {
    query
  } = queryConfig;
  return apiClientWithMiddleware(clientConfig).post(`${NOTIFICATION_STATION_API_V3_URL}/all/action`, {
    data: {
      action,
      excludedIds: notificationIds,
      sidebarState,
      types: [filterType]
    },
    query
  });
};
export const singleActionOnNotification = ({
  action,
  notificationId
}) => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.reject(new Error('Undefined portal'));
  }
  const {
    query
  } = queryConfig;
  return apiClientWithMiddleware(clientConfig).post(`${NOTIFICATION_STATION_API_V3_URL}/${notificationId}/action`, {
    data: {
      action,
      notificationId
    },
    query
  });
};
// Base function for all count APIs
const getCountBase = queryParams => {
  const {
    queryConfig,
    clientConfig
  } = getApiConfigs();
  if (!queryConfig.query.portalId) {
    captureUndefinedPortalError();
    return Promise.reject(new Error('Undefined portal'));
  }
  const url = `${NOTIFICATION_STATION_API_V3_URL}/count`;
  return apiClientWithMiddleware(clientConfig).get(url, {
    query: Object.assign({
      portalId: queryConfig.query.portalId
    }, queryParams)
  });
};
export const getUnreadCount = () => {
  return getCountBase({
    showUnreadOnly: true
  });
};
export const getAllCount = () => {
  return getCountBase({
    sidebarState: 'ALL'
  });
};
export const getDeletedCount = () => {
  return getCountBase({
    sidebarState: 'TRASH'
  });
};